import { forwardRef, useContext } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp, InfoDialog } from '@pega/cosmos-react-core';
import type {
  BaseProps,
  RefElement,
  InfoDialogProps,
  NoChildrenProp,
  OmitStrict,
  WithAttributes
} from '@pega/cosmos-react-core';
import { RichTextViewer } from '@pega/cosmos-react-rte';
import { StyledEditorContainer } from '@pega/cosmos-react-rte/lib/components/RichTextEditor/RichTextEditor.styles';

import { FeedContext } from '../Feed/Feed.context';

import AutopilotAvatar from './AutopilotAvatar';

export type AutopilotGlimpseProps = WithAttributes<
  'div',
  BaseProps &
    OmitStrict<InfoDialogProps, 'children' | 'progress'> &
    NoChildrenProp & {
      /** Ref for the wrapping element. */
      ref?: Ref<HTMLDivElement>;
    }
>;

const StyledInfoDialog = styled(InfoDialog)(({ theme }) => {
  return css`
    ${StyledEditorContainer} {
      padding: 0;
      max-width: min(${theme.base['content-width'].sm}, calc(100vw - 2rem));
    }
  `;
});

StyledInfoDialog.defaultProps = defaultThemeProp;

export default forwardRef<
  RefElement<AutopilotGlimpseProps>,
  PropsWithoutRef<AutopilotGlimpseProps>
>(function AutopilotGlimpse(
  { target, onDismiss, ...restProps }: PropsWithoutRef<AutopilotGlimpseProps>,
  ref: AutopilotGlimpseProps['ref']
) {
  const { autopilotInfo } = useContext(FeedContext);

  return (
    <StyledInfoDialog
      {...restProps}
      target={target}
      heading={{
        primary: autopilotInfo.label,
        secondary: autopilotInfo.meta,
        visual: <AutopilotAvatar />
      }}
      onDismiss={onDismiss}
      ref={ref}
    >
      {autopilotInfo.description && (
        <RichTextViewer type='markdown' content={autopilotInfo.description} />
      )}
    </StyledInfoDialog>
  );
});
